import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useRenderPdfPreview } from '@/api/rpc/hooks/communications/usePdfTemplates';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PdfPageSetup } from '@erp/shared-types';
import {
  applyPageSetupToSpec,
  parsePdfSpec,
  parseJsonRecord,
} from './pdfTemplateJsonSpec';

interface PdfJsonTemplateEditorProps {
  jsonSpec: string;
  onJsonSpecChange: (value: string) => void;
  sampleVariables: string;
  onSampleVariablesChange: (value: string) => void;
  /** Page setup from the form controls, merged into the spec's Page node so it drives the preview. */
  pageSetup: PdfPageSetup;
  /** Form config (page setup, variables, …) rendered at the top of the left/config pane. */
  configSlot?: ReactNode;
  /** When true, the spec and sample-variable textareas are not editable. */
  readOnly?: boolean;
  expanded?: boolean;
}

export const DEFAULT_SAMPLE_VARIABLES = `{
  "companyName": "Northwind Supply Co.",
  "customerName": "Acme Produce",
  "invoiceNumber": "INV-1001",
  "issuedDate": "May 1, 2026",
  "dueDate": "May 15, 2026",
  "amountDue": "$1,250.00",
  "lineItems": [
    {
      "sku": "CASE-APPLES",
      "description": "Organic apples, 40 lb cases",
      "quantity": 12,
      "total": "$720.00"
    },
    {
      "sku": "CASE-PEARS",
      "description": "Green pears, 40 lb cases",
      "quantity": 8,
      "total": "$480.00"
    }
  ]
}`;

export function PdfJsonTemplateEditor({
  jsonSpec,
  onJsonSpecChange,
  sampleVariables,
  onSampleVariablesChange,
  pageSetup,
  configSlot,
  readOnly = false,
  expanded = false,
}: PdfJsonTemplateEditorProps) {
  const gt = useGT();
  const renderPreview = useRenderPdfPreview();
  const { isPending, mutate } = renderPreview;
  const requestIdRef = useRef(0);
  const [isVariablesOpen, setIsVariablesOpen] = useState(false);
  const [previewPdfBase64, setPreviewPdfBase64] = useState('');
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const timeout = window.setTimeout(() => {
      const specResult = parsePdfSpec(jsonSpec);
      if (specResult.error || !specResult.data) {
        setPreviewPdfBase64('');
        setPreviewError(specResult.error ?? 'Invalid JSON.');
        return;
      }

      const variablesResult = parseJsonRecord(sampleVariables);
      if (variablesResult.error || !variablesResult.data) {
        setPreviewPdfBase64('');
        setPreviewError(
          gt('Sample variables JSON is invalid: {message}', {
            context: 'pdf template preview validation error',
            message: variablesResult.error ?? 'Invalid JSON.',
          }),
        );
        return;
      }

      mutate(
        {
          jsonSpec: applyPageSetupToSpec(specResult.data, pageSetup),
          variables: variablesResult.data,
        },
        {
          onSuccess: (result) => {
            if (requestIdRef.current !== requestId) return;
            setPreviewPdfBase64(result.pdfBase64);
            setPreviewError('');
          },
          onError: (error) => {
            if (requestIdRef.current !== requestId) return;
            setPreviewPdfBase64('');
            setPreviewError(error.message);
          },
        },
      );
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [gt, jsonSpec, mutate, sampleVariables, pageSetup]);

  return (
    // The height lives on this wrapper, not the panel group: react-resizable-
    // panels forces the group to height:100%, which would otherwise collapse to
    // the min-height and ignore the viewport calc.
    <div
      className={cn(
        expanded ? 'h-[calc(100vh-27rem)] min-h-[500px]' : 'h-[600px]',
      )}
    >
      <ResizablePanelGroup
        direction="horizontal"
        className="h-full rounded-md border"
      >
        {/* Config pane: form config + document spec + sample variables. */}
        <ResizablePanel defaultSize={42} minSize={28}>
          <div className="flex h-full min-h-0 flex-col">
            {configSlot && (
              <div className="max-h-[55%] shrink-0 overflow-y-auto border-b p-3">
                {configSlot}
              </div>
            )}
            <div className="border-b px-3 py-2 text-sm font-medium">
              <T context="pdf template editor panel title">Document Spec</T>
            </div>
            <Textarea
              value={jsonSpec}
              onChange={(event) => onJsonSpecChange(event.target.value)}
              placeholder={gt('Paste a PDF document spec here', {
                context: 'pdf template editor placeholder',
              })}
              className="min-h-0 flex-1 resize-none rounded-none border-0 font-mono text-xs focus-visible:ring-0"
              spellCheck={false}
              readOnly={readOnly}
            />
            <Collapsible
              open={isVariablesOpen}
              onOpenChange={setIsVariablesOpen}
              className="border-t"
            >
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-1 px-3 py-2"
                >
                  {isVariablesOpen ? (
                    <CaretDownIcon className="h-3.5 w-3.5" />
                  ) : (
                    <CaretRightIcon className="h-3.5 w-3.5" />
                  )}
                  <T context="pdf template sample data section label">
                    Sample Variables
                  </T>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 border-t p-3">
                <Label htmlFor="pdfJsonSampleVariables" className="text-xs">
                  <T context="pdf template sample data textarea label">
                    Preview data passed as $state
                  </T>
                </Label>
                <Textarea
                  id="pdfJsonSampleVariables"
                  value={sampleVariables}
                  onChange={(event) =>
                    onSampleVariablesChange(event.target.value)
                  }
                  className="min-h-28 font-mono text-xs"
                  spellCheck={false}
                  readOnly={readOnly}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Preview pane. */}
        <ResizablePanel defaultSize={58} minSize={30}>
          <div className="flex h-full min-h-0 flex-col bg-muted/20">
            <div className="flex items-center gap-2 border-b bg-background px-3 py-2 text-sm font-medium">
              <T context="pdf template preview panel title">Live Preview</T>
              {isPending && <LoadingSpinner inline size="sm" />}
            </div>
            <div className="relative min-h-0 flex-1 bg-background">
              {isPending && !previewPdfBase64 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
                  <LoadingSpinner />
                </div>
              )}
              {previewError ? (
                <div className="whitespace-pre-wrap p-3 text-sm text-destructive">
                  {previewError}
                </div>
              ) : previewPdfBase64 ? (
                <iframe
                  title={gt('PDF template preview', {
                    context: 'pdf template preview iframe title',
                  })}
                  src={`data:application/pdf;base64,${previewPdfBase64}`}
                  className="h-full w-full border-0 bg-white"
                />
              ) : (
                <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
                  <T context="pdf template empty preview message">
                    Enter a valid document spec to render a preview.
                  </T>
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
